import { useMutation, useQueryClient } from "@tanstack/react-query";

import { SHIPMENT_TOAST_MESSAGES } from "~/features/shipment-management/constants/toast-messages.ts";
import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { markLoadedResponseSchema } from "~/shared/schemas/shipment-management-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

export type MarkLoadedRequest = {
	completedTime: string;
	proNumber?: string;
};

export const postMarkLoaded = async (
	poNumber: string,
	body: MarkLoadedRequest,
) => {
	const url = apiRoutes.POSTMARKLOADED.replace(":poNumber", poNumber);
	const resp = await postAndValidate(url, body, markLoadedResponseSchema);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePostMarkLoaded = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();

	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation({
		mutationKey: ["markLoaded"],
		mutationFn: ({
			poNumber,
			...body
		}: { poNumber: string } & MarkLoadedRequest) =>
			postMarkLoaded(poNumber, body),
		// Awaiting the refetch keeps the mutation pending (and the spinner up)
		// until the shipment reflects the new state; toast once it's done.
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["shipment"] });
			showSuccess(SHIPMENT_TOAST_MESSAGES.shipmentMarkedLoaded);
		},
		onError: (err) => {
			showError(
				err instanceof Error
					? err.message
					: "Failed to mark shipment as loaded. Please try again.",
			);
		},
	});

	return {
		markLoaded: mutate,
		markLoadedAsync: mutateAsync,
		isSavingMarkLoaded: isPending,
		error,
		data,
		resetError: reset,
	};
};
